'use es6';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { PropertyDataSensitivity } from 'customer-data-objects/property/PropertyDataSensitivity';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { RequestStatus } from '../../api/constants';
import { isUserProperty, propertyCanHaveDefaultValue, getUseLocalDefaultValue, getIsPropertyOnlyToken, getDefaultValue, i18nKey } from '../../utils/properties';
import { useProperties } from './hooks/useProperties';
import { useOwners } from './hooks/useOwners';
import TokenBrowser from './TokenBrowser';
import TokenEditor from './TokenEditor';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EDIT_STEP = 'fallback';
const BROWSE_STEP = 'token';
const canInsertProperty = (currentOption, selectedUserProperty, allowEmptyFallbacks, isGlobalFallback, localDefaultValue) => {
  const selectedCategory = currentOption.category;
  const selectedProperty = currentOption.name;
  const categoryAndPropertyValid = !!(selectedCategory && selectedProperty && (!isUserProperty(currentOption) || selectedUserProperty));
  const defaultsValid = !!(allowEmptyFallbacks || isGlobalFallback || localDefaultValue);
  return categoryAndPropertyValid && defaultsValid;
};
const getHydratedProperty = property => {
  let isPropertyOnlyToken = false;
  let token = '';
  if (!property) {
    return undefined;
  }
  if (getIsPropertyOnlyToken(property.category, property.property)) {
    isPropertyOnlyToken = true;
    token = property.property;
  } else {
    token = `${property.category}.${property.property}`;
  }
  return {
    isPropertyOnlyToken,
    token
  };
};
const isSelectedPropertyEditable = currentOption => {
  return isUserProperty(currentOption) || propertyCanHaveDefaultValue(currentOption);
};
const renderLoading = (isLoading, currentStep) => {
  if (!isLoading) {
    return null;
  }
  const minHeight = currentStep === EDIT_STEP ? 200 : 320;
  return /*#__PURE__*/_jsx(UILoadingSpinner, {
    layout: "centered",
    minHeight: minHeight
  });
};
const renderTokenBrowser = (props, currentOption, currentStep, handleTokenChange, propertiesLoading, propertyOptions) => {
  if (propertiesLoading || currentStep !== BROWSE_STEP) {
    return null;
  }
  const {
    dropdownClassName,
    emailSettingsLink,
    personalizeConfig
  } = props;
  return /*#__PURE__*/_jsx(TokenBrowser, {
    currentOption: currentOption,
    dropdownClassName: dropdownClassName,
    emailSettingsLink: emailSettingsLink,
    onPropertySelect: handleTokenChange,
    propertyOptions: propertyOptions,
    personalizeConfig: personalizeConfig
  });
};
const renderTokenEditor = (props, currentOption, currentStep, fallback, handleBackClick, handleUserPropertySelect, isGlobalFallback, owners, propertiesLoading, propertyOptions, selectedUserProperty, setFallback, setIsGlobalFallback) => {
  const {
    dropdownClassName,
    tooltipClassName,
    emailSettingsLink,
    personalizeConfig,
    publishingSettingsLink,
    siteSettings
  } = props;
  if (propertiesLoading || currentStep !== EDIT_STEP || personalizeConfig.hideFallbacks) {
    return null;
  }
  const enableFallback = propertyCanHaveDefaultValue(currentOption, selectedUserProperty);
  return /*#__PURE__*/_jsx(TokenEditor, {
    currentOption: currentOption,
    defaultValue: getDefaultValue(currentOption.name, currentOption.category, siteSettings, personalizeConfig.isEmail),
    dropdownClassName: dropdownClassName,
    enableFallback: enableFallback,
    fallback: fallback,
    isGlobalFallback: isGlobalFallback,
    isUserProperty: isUserProperty(currentOption),
    onBackClick: handleBackClick,
    onFallbackChange: setFallback,
    onIsGlobalFallbackChange: setIsGlobalFallback,
    onUserPropertySelect: handleUserPropertySelect,
    owners: owners,
    propertyOptions: propertyOptions,
    settingsLink: personalizeConfig.isEmail ? emailSettingsLink : publishingSettingsLink,
    userProperty: selectedUserProperty,
    tooltipClassName: tooltipClassName
  });
};
const renderButtons = (props, allowEmptyFallbacks, currentOption, currentStep, fallback, handleInsertToken, isGlobalFallback, propertiesLoading, selectedUserProperty) => {
  if (propertiesLoading || currentStep !== EDIT_STEP) {
    return null;
  }
  const {
    isInsert,
    onClose
  } = props;
  const handleInsertClick = () => {
    handleInsertToken(currentOption);
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "m-bottom-5 m-top-3 m-x-5",
    children: [/*#__PURE__*/_jsx(UIButton, {
      use: "tertiary",
      "data-test-id": "insert-token-confirm-button",
      onClick: handleInsertClick,
      disabled: !canInsertProperty(currentOption, selectedUserProperty, allowEmptyFallbacks, isGlobalFallback, fallback),
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: isInsert ? i18nKey('insertToken') : i18nKey('editToken')
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      use: "tertiary-light",
      "data-test-id": "insert-token-cancel-button",
      onClick: onClose,
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('cancel')
      })
    })]
  });
};
export default function PersonalizeBrowser(props) {
  const {
    onClose,
    onInsert,
    fetchProperties,
    personalizeConfig,
    property,
    siteSettings
  } = props;
  const allowEmptyFallbacks = personalizeConfig.allowEmptyFallbacks === undefined ? true : personalizeConfig.allowEmptyFallbacks;
  const [propertyOptions, propertyRequestStatus] = useProperties(fetchProperties);
  const owners = useOwners(props.fetchOwners);
  const [currentOption, setCurrentOption] = useState(property);
  const [fallback, setFallback] = useState(property.fallback);
  const [isGlobalFallback, setIsGlobalFallback] = useState(property.fallback === undefined);
  const [selectedUserProperty, setUserProperty] = useState('');
  const [currentStep, setCurrentStep] = useState(property.category ? EDIT_STEP : BROWSE_STEP);
  useEffect(() => {
    const hydratedProperty = getHydratedProperty(property);
    const hydratedOption = propertyOptions.reduce((acc, category) => {
      return [...acc, ...category.options];
    }, []).find(option => {
      const value = hydratedProperty.isPropertyOnlyToken ? option.value.split('.')[1] : option.value;
      return value === hydratedProperty.token;
    });
    if (hydratedOption) {
      setCurrentOption(hydratedOption);
      const {
        dataSensitivity
      } = hydratedOption.originalProperty || {};
      if (personalizeConfig.hideFallbacks || dataSensitivity === PropertyDataSensitivity.STANDARD || dataSensitivity === PropertyDataSensitivity.HIGH) {
        setCurrentStep(BROWSE_STEP);
      } else {
        const step = isSelectedPropertyEditable(hydratedOption) ? EDIT_STEP : BROWSE_STEP;
        setCurrentStep(step);
        setFallback(property.fallback);
        setIsGlobalFallback(property.fallback === undefined);
      }
      if (property.subCategory) {
        setUserProperty(property.subCategory);
      }
    }
  }, [personalizeConfig.hideFallbacks, property, propertyOptions]);
  const handleInsertToken = selectedOption => {
    const excludeFallback = isGlobalFallback || personalizeConfig.hideFallbacks || !propertyCanHaveDefaultValue(selectedOption, selectedUserProperty);
    const fallbackValue = excludeFallback ? null : fallback || '';
    onInsert(selectedOption, selectedUserProperty, fallbackValue);
    onClose();
  };
  const handleTokenChange = ({
    target: {
      value
    }
  }) => {
    const shouldSkipEditStep = !isSelectedPropertyEditable(value) || personalizeConfig.hideFallbacks;
    if (shouldSkipEditStep) {
      handleInsertToken(value);
    } else {
      setCurrentOption(value);
      setCurrentStep(EDIT_STEP);
      setUserProperty('');
      setIsGlobalFallback(!getUseLocalDefaultValue({
        defaultValue: getDefaultValue(value.name, value.category, siteSettings, personalizeConfig.isEmail),
        currentOption: value
      }));
    }
  };
  const handleUserPropertySelect = ({
    target: {
      value
    }
  }) => {
    setCurrentStep(EDIT_STEP);
    setUserProperty(value);
  };
  const handleBackClick = () => {
    setCurrentStep(BROWSE_STEP);
  };
  const propertiesLoading = propertyRequestStatus === RequestStatus.PENDING || propertyRequestStatus === RequestStatus.UNINITIALIZED;
  return /*#__PURE__*/_jsxs(UIFieldset, {
    "data-test-id": "personalize-browser-popover-body",
    _size: "small",
    children: [renderLoading(propertiesLoading, currentStep), renderTokenBrowser(props, currentOption, currentStep, handleTokenChange, propertiesLoading, propertyOptions), renderTokenEditor(props, currentOption, currentStep, fallback, handleBackClick, handleUserPropertySelect, isGlobalFallback, owners, propertiesLoading, propertyOptions, selectedUserProperty, setFallback, setIsGlobalFallback), renderButtons(props, allowEmptyFallbacks, currentOption, currentStep, fallback, handleInsertToken, isGlobalFallback, propertiesLoading, selectedUserProperty)]
  });
}
PersonalizeBrowser.propTypes = {
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  fetchProperties: PropTypes.func,
  fetchOwners: PropTypes.func,
  personalizeConfig: PropTypes.shape({
    // Move constants to rich text lib
    hideFallbacks: PropTypes.bool,
    hideSiteSettingsCategory: PropTypes.bool,
    includeSubscriptionTypeCategory: PropTypes.bool,
    includeInternalContactRecord: PropTypes.bool,
    allowEmptyFallbacks: PropTypes.bool,
    isEmail: PropTypes.bool
  }),
  property: PropTypes.shape({
    category: PropTypes.string.isRequired,
    property: PropTypes.string.isRequired,
    fallback: PropTypes.string,
    subCategory: PropTypes.string,
    dataSensitivity: PropTypes.string
  }),
  siteSettings: PropTypes.shape({
    mergeTagDefaults: PropTypes.objectOf(PropTypes.string),
    pageMergeTagDefaults: PropTypes.objectOf(PropTypes.string),
    accountMergeTagDefaults: PropTypes.objectOf(PropTypes.string),
    pageAccountMergeTagDefaults: PropTypes.objectOf(PropTypes.string)
  })
};
PersonalizeBrowser.defaultProps = {
  isInsert: true,
  property: {
    fallback: undefined,
    category: '',
    property: '',
    subCategory: ''
  }
};